import type { NodeTypes } from '@xyflow/react';
import { NoteNode, noteNodeMeta } from './note';
import { ContainerNode, containerNodeMeta } from './container';
import { FragmentNode, fragmentNodeMeta } from './fragment';
import { HubNode, hubNodeMeta } from './hub';
import { CommentNode, commentNodeMeta } from './comment';
import { GroupNode, groupNodeMeta } from './group';
import { EntryNode, entryNodeMeta } from './entry';
import { ExitNode, exitNodeMeta } from './exit';
import { ImageNode, imageNodeMeta } from './image';
import { ConditionNode, conditionNodeMeta } from './condition';
import { InstructionNode, instructionNodeMeta } from './instruction';
import { TilemapRefNode, tilemapRefNodeMeta } from './tilemapRef';

/**
 * Node Type Registry Definition Array
 *
 * Centralized list of all registered node types in the system. This array is the
 * single source of truth for node type metadata, schemas, and display information.
 *
 * Each entry contains:
 * - `type`: unique identifier for the node type
 * - `label`: user-facing name shown in palette and inspector
 * - `icon`: emoji or short symbol for visual identification
 * - `defaultData`: initial data object for new instances
 * - `description`: tooltip and documentation text
 * - `category`: grouping for palette organization
 *
 * **Order matters for palette organization:**
 * Entries earlier in the array appear first in the palette UI.
 * Nodes are grouped by category for the palette:
 * - narrative: story content (fragments, hubs, dialogue)
 * - logic: flow control (conditions, instructions)
 * - container: structural (containers, groups)
 * - structural: entry/exit, references
 * - utility: images, comments, notes
 *
 * @constant
 */
export const nodeTypeDefs = [
    // Narrative nodes
    fragmentNodeMeta,
    hubNodeMeta,
    // Logic nodes (Phase 6)
    conditionNodeMeta,
    instructionNodeMeta,
    // Container and structural nodes
    containerNodeMeta,
    entryNodeMeta,
    exitNodeMeta,
    // Game nodes
    tilemapRefNodeMeta,
    // Utility nodes
    imageNodeMeta,
    commentNodeMeta,
    groupNodeMeta,
    noteNodeMeta,
] as const;

export type RegisteredNodeType = (typeof nodeTypeDefs)[number]['type'];

/**
 * React Flow Node Types Map
 *
 * Maps node type identifiers to their React components for rendering on the canvas.
 * React Flow uses this to determine which component to render for each node.
 *
 * The keys must exactly match the `type` field in node objects.
 *
 * @constant
 */
export const nodeTypes: NodeTypes = {
    fragment: FragmentNode,
    hub: HubNode,
    comment: CommentNode,
    group: GroupNode,
    container: ContainerNode,
    entry: EntryNode,
    exit: ExitNode,
    image: ImageNode,
    note: NoteNode,
    condition: ConditionNode,
    instruction: InstructionNode,
    tilemap_ref: TilemapRefNode,
};

export const paletteItems = nodeTypeDefs.map((def) => ({
    type: def.type,
    label: def.label,
    icon: def.icon,
}));

export function defaultDataFor(type: string): Record<string, unknown> | undefined {
    const def = nodeTypeDefs.find((d) => d.type === type);
    return def ? { ...def.defaultData } : undefined;
}

/**
 * Node metadata `icon` values are a mix of Tabler icon classes (e.g.
 * `'ti-folder'`) and raw emoji/symbol characters (e.g. `'📦'`, `'◇'`) —
 * see the `icon:` field in each nodeTypes/*.tsx file. Consumers rendering
 * these (Palette.tsx, canvas/ContextMenu.tsx) need to tell them apart:
 * a Tabler class belongs in an `<i className="ti {icon}">`, while an
 * emoji/symbol should be rendered as plain text instead.
 */
export function isTablerIconClass(icon: string): boolean {
    return icon.startsWith('ti-');
}
